import { Allow, parse } from 'partial-json';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';

const requiresNodeId = (action: string) => ACTIONS_REQUIRING_NODE_ID.has(action);
const ACTIONS_REQUIRING_NODE_ID = new Set([
	'replaceNode',
	'insertNodeBefore',
	'insertNodeAfter',
	'removeNode',
	'wrap',
]);

export class ConfluencePageCreateCommandProcessor {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private actionId: string | undefined;
	private prevAdf: string = '';
	private streamer: ADFStreamer | undefined;

	constructor(editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>) {
		this.editorApi = editorApi;
	}

	private init() {
		this.actionId = this.editorApi.aiStreamingOrchestrator?.actions.initAction({
			type: 'appendNode',
		});
		if (!this.actionId) {
			throw new Error('Failed to initialize streaming action for create');
		}

		const schema = this.editorApi.core.sharedState.currentState()?.schema;
		if (schema) {
			this.streamer = new ADFStreamer(schema);
		}
	}

	stream(adf: string) {
		if (!this.actionId) {
			this.init();
		}

		if (!adf) {
			return;
		}

		const nextChunk = adf.substring(this.prevAdf.length);
		if (this.streamer) {
			this.streamer.processChunk(nextChunk);
		}
		this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId!, nextChunk);
		this.prevAdf = adf;
	}

	finish() {
		if (this.actionId) {
			this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
			this.actionId = undefined;
			this.prevAdf = '';
			if (this.streamer) {
				return this.streamer.getLastDoc();
			}
		}
	}
}

class ToolCallProcessor {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private actionId: string;

	constructor(
		editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>,
		command: StreamingCommand,
	) {
		this.editorApi = editorApi;
		const actionId = this.editorApi?.aiStreamingOrchestrator?.actions.initAction(command);
		if (!actionId) {
			throw new Error('Failed to initialize streaming action for update');
		}
		this.actionId = actionId;
	}

	stream(adf: string) {
		if (!adf) {
			return;
		}

		this.editorApi?.aiStreamingOrchestrator?.actions.streamValue(this.actionId, adf);
	}

	finish() {
		this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId!);
	}
}

export class ConfluencePageUpdateCommandProcessor {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private currentToolCallIndex: number | undefined;

	constructor(editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>) {
		this.editorApi = editorApi;
	}

	parseToolCallAndStream(toolCall: any) {
		const value = toolCall?.arguments?.value;
		const action = toolCall?.name;

		let localId: string | undefined;
		if (requiresNodeId(action)) {
			localId = toolCall?.arguments?.localId;
			if (!localId) {
				return;
			}
		}

		let command: StreamingCommand;
		switch (action) {
			case 'replaceNode':
			case 'insertNodeBefore':
			case 'insertNodeAfter':
			case 'removeNode':
				command = {
					type: action,
					localId,
				};
				break;
			case 'appendNode':
				command = {
					type: action,
				};
				break;
			case 'wrap':
				const wrapperType = toolCall?.arguments?.wrapperType;
				const wrapperAttrs = toolCall?.arguments?.wrapperAttrs;
				if (!wrapperType || !wrapperAttrs) {
					return;
				}

				command = {
					type: action,
					localId,
					wrapperType,
					wrapperAttrs: JSON.parse(wrapperAttrs),
				};
				break;
			default:
				return;
		}

		const toolCallProcessor = new ToolCallProcessor(this.editorApi, command);
		toolCallProcessor.stream(value);
		toolCallProcessor.finish();
	}

	stream(document: string, isFinished: boolean) {
		let toolCalls: any[];

		try {
			toolCalls = parse(document, Allow.ALL) ?? [];
		} catch {
			return;
		}

		if (this.currentToolCallIndex === undefined) {
			if (toolCalls.length > 0) {
				this.currentToolCallIndex = 0;
			} else {
				// In case the parsed tool calls are empty
				return;
			}
		}

		// Unless we know we have the last message, only process up to the
		// penultimate tool call so that we can guarantee the data is complete.
		const endIndex = isFinished ? toolCalls.length : toolCalls.length - 1;

		for (; this.currentToolCallIndex < endIndex; this.currentToolCallIndex++) {
			this.parseToolCallAndStream(toolCalls[this.currentToolCallIndex]);
		}
	}

	finish() {
		this.currentToolCallIndex = undefined;
	}
}
